"use client";
import { useSelection } from "@frend-digital/centra/client";
import { events } from "@frend-digital/events";
import clsx from "clsx";
import { default as Image } from "next/image";
import type React from "react";
import { useRef, useState } from "react";
import { FavouriteIcon } from "@/components/icons/FavouriteIcon";
import Plus from "@/components/icons/Plus";
import Button from "@/components/ui/Button";
import { PrefetchLink } from "@/components/ui/PrefetchLink";
import Video from "@/components/ui/Video";
import useDevice from "@/hooks/useDevice";
import { useLocale, useTranslations } from "@/i18n/client";
import type { ProductCard as PC } from "@/lib/centra/formatters";
import { calculateDiscountPercentage } from "@/lib/centra/formatters/utils";
import { useWishlist } from "@/lib/centra/hooks";
import { sizes } from "@/lib/utils/imageSizes";
import { getProductLink } from "../../ProductProvider";
import Price from "../Price/Price";
import styles from "./index.module.css";

const ProductCard = ({
  product,
  related = false,
  className,
  priority,
}: {
  product: PC;
  related?: boolean;
  className?: string;
  priority?: boolean;
}) => {
  const locale = useLocale();
  const [activeVariant, setActiveVariant] = useState(0);
  const linkRef = useRef<HTMLAnchorElement>(null!);
  const t = useTranslations();
  const { mobile: isMobile } = useDevice();
  const slug = product?.core?.slug ?? "#";

  if (!product) {
    return null;
  }

  const itemSet = new Set();
  const variants = product.variants?.filter(card => {
    // If related product has a set gender, & current card does not, filter it out
    if (card.gender && !product.gender) return false;

    // If product has a set gender, filter out cards that don't match
    if (card.gender !== product.gender) return false;

    // If product is or is not archived, filter out cards that don't match
    if (product.isArchive !== card.isArchive) return false;

    if (itemSet.has(card.itemId)) return false;

    itemSet.add(card.itemId);
    return true;
  });

  const variant = variants?.at(activeVariant);
  const discountPercent = variant?.prices?.[0]?.discountPercent;
  const hasDiscount = discountPercent && discountPercent !== 0;
  const discountPercentageString = discountPercent
    ? `-${discountPercent}%`
    : "";

  const name = product.core?.name;

  const hasTags =
    variants?.[activeVariant]?.comingSoon || variants?.[activeVariant]?.soldOut;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    /**
     * Prevent navigating to the product if a button or internal link is pressed
     */
    if (target.closest("a") || target.closest("button")) {
      return;
    }

    linkRef.current.click();
  };

  const isForcedSalePrice = !!variant?.isForcedSalePrice;

  return (
    <article
      className={clsx(
        styles.card__container,
        related && styles.related,
        className,
      )}
    >
      <div onClick={handleClick} className={`product-card ${styles.card}`}>
        <div className={styles.features}>
          {product.features?.map((feature, i) => {
            if (!feature.displayIconOnCard) return;

            return (
              <Image key={i} height={25} width={25} alt="" src={feature.icon} />
            );
          })}
        </div>
        <PrefetchLink
          ref={linkRef}
          title={`${name} - ${variant?.variantName}`}
          href={
            activeVariant === 0
              ? getProductLink({ locale, slug })
              : getProductLink({
                  locale,
                  slug,
                  variantId: variant?.itemId,
                })
          }
          depictListingResultId={product.depictListingResultId}
          depictSearchResultId={product.depictSearchResultId}
          depictProductId={product.depictProductId}
          className={clsx(styles.card__image)}
        >
          {variant?.labels && (
            <div className={styles.tags}>
              {variant?.labels.map((label: string, key) => (
                <span className={clsx(styles.tag)} key={key}>
                  {label}
                </span>
              ))}
            </div>
          )}
          {variants &&
            variants.map((variant, index) => {
              const mainImage = variant.media?.[0];
              const secondaryImage = variant.media?.[1];

              return (
                <div
                  key={index}
                  className={clsx(
                    styles.card__image__inner,
                    index === activeVariant && styles.active,
                  )}
                >
                  {hasDiscount && isForcedSalePrice ? (
                    <>
                      <div className={styles.sale}>
                        {discountPercentageString}
                      </div>
                      <span className="sr-only">
                        {discountPercentageString}
                      </span>
                    </>
                  ) : null}
                  {mainImage && (
                    <>
                      {!!product.showVideo && !!product.videoUrl && (
                        <Video
                          url={product.videoUrl as string}
                          playing
                          loop
                          muted
                          width="100%"
                          height="100%"
                          className={clsx(styles.product__image, styles.main)}
                        />
                      )}
                      {!product.showVideo && (
                        <Image
                          src={mainImage.href}
                          alt={mainImage.alt}
                          fill
                          sizes={sizes(50)}
                          className={clsx(styles.product__image, styles.main)}
                          fetchPriority={index === 0 ? "high" : undefined}
                          priority={index === 0 ? priority : undefined}
                        />
                      )}
                    </>
                  )}
                  {secondaryImage && (
                    <Image
                      src={
                        product.showVideo ? mainImage.href : secondaryImage.href
                      }
                      alt={
                        product.showVideo ? mainImage.alt : secondaryImage.alt
                      }
                      fill
                      sizes={sizes(50)}
                      className={clsx(styles.product__image, styles.secondary)}
                      fetchPriority={index === 0 ? "high" : undefined}
                      priority={index === 0 ? priority : undefined}
                    />
                  )}
                </div>
              );
            })}
        </PrefetchLink>
        <div className={styles.card__description}>
          {!related && (
            <div className={styles.addFavoriteWrapper}>
              <div className={styles.card__swatches}>
                <>
                  {variants?.map((variant, key) => {
                    if (key >= 5) return;
                    if (variants && variants.length > 5 && key === 4) {
                      return (
                        <Button
                          prefetch="hover"
                          mode="link"
                          className={styles.plusIcon}
                          href={getProductLink({
                            locale,
                            slug,
                            variantId: variant.itemId,
                          })}
                          key={key}
                        >
                          <Plus />
                        </Button>
                      );
                    } else {
                      return (
                        <PrefetchLink
                          title={variant.variantName}
                          href={getProductLink({
                            locale,
                            slug,
                            variantId: variant.itemId,
                          })}
                          key={key}
                          className={clsx(
                            styles.swatch,
                            key === activeVariant && styles.active,
                          )}
                          onMouseEnter={() => {
                            !isMobile && setActiveVariant(key);
                          }}
                        >
                          <span
                            style={{ background: `${variant?.swatch?.hex}` }}
                          />
                        </PrefetchLink>
                      );
                    }
                  })}
                </>
              </div>
              {product.id && <WishlistButton product={product} />}
            </div>
          )}
          <p className={styles.product__material}>{name}</p>
          <div className={styles.product__meta}>
            <Price
              isForcedSalePrice={isForcedSalePrice}
              price={{
                discountPercent: calculateDiscountPercentage(
                  product?.priceBeforeDiscountAsNumber!,
                  product?.priceAsNumber!,
                ),
                price: product?.price!,
                priceAsNumber: product?.priceAsNumber as number,
                priceBeforeDiscount: product?.priceBeforeDiscount as string,
                priceBeforeDiscountAsNumber:
                  product?.priceBeforeDiscountAsNumber as number,
                productOnSale:
                  product.showAsOnSale ??
                  product.priceAsNumber !== product.priceBeforeDiscountAsNumber,
              }}
            />
          </div>
          {!!hasTags && (
            <div className={styles.tags}>
              {product?.isNew && (
                <div className={clsx(styles.card__tag)}>
                  <p>{t("product.tags.new")}</p>
                </div>
              )}
              {variants?.[activeVariant]?.soldOut &&
                !variants?.[activeVariant]?.comingSoon && (
                  <div className={clsx(styles.card__tag, styles.soldOutTag)}>
                    <p>{t("product.tags.soldOut")}</p>
                  </div>
                )}
              {variants?.[activeVariant]?.comingSoon &&
                !product?.description?.price?.[0]?.productOnSale && (
                  <div
                    className={clsx(styles.card__tag, styles.commingSoonTag)}
                  >
                    {t("product.tags.comingSoon")}
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default ProductCard;

const WishlistButton = ({ product }: { product: PC }) => {
  const { data } = useSelection();
  const { has, add, remove, isLoading } = useWishlist();
  const itemInWishlist = has(product?.itemId);

  if (isLoading) return null;
  return (
    <Button
      mode="icon"
      aria-label="Add to wishlist"
      className={styles.addButton}
      onClick={() => {
        if (itemInWishlist) {
          return remove(product?.itemId);
        }
        add(product?.itemId);

        events.dispatch("add_to_wishlist", data, {
          dispatcher: "centra",
          item_id: product?.itemId,
        });
      }}
    >
      <div
        className={clsx(
          itemInWishlist ? styles.removeFavourite : styles.addFavourite,
        )}
      >
        <FavouriteIcon />
      </div>
    </Button>
  );
};
